import React from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import "flatpickr/dist/plugins/monthSelect/style.css";

const FormDatePicker = ({
  field,
  value,
  handleChange,
  error = null,
  mode = "date", // 'date', 'datetime', 'monthYear', 'time'
  selectMode = "single", // 'single', 'multiple', 'range'
  disablePast = false,
  disablePastMonth = false,
  yearLimit = null, //{ prev: 1, next: 2 }
  lableHide = false,
  dateFormat = "Y-m-d",
  ...props
}) => {
  const options = {
    minDate: field.minDate || null,
    maxDate: field.maxDate || null,
    disable: field.disable || [],
    closeOnSelect: false,
    closeOnDateSelect: false,
    dateFormat: dateFormat,
  };

  const now = new Date();
  const currentYear = now.getFullYear();

  // Manage yearLimit only by year (not months or days)
  if (yearLimit) {
    const prevLimit = yearLimit.prev ?? 0;
    const nextLimit = yearLimit.next ?? 0;

    const minDate = new Date(currentYear - prevLimit, 0, 1);
    const maxDate = new Date(currentYear + nextLimit, 11, 31);

    options.minDate = minDate;
    options.maxDate = maxDate;
  }

  // Handle disablePast or disablePastMonth
  if (disablePast || disablePastMonth) {
    if (mode === "monthYear") {
      options.minDate = new Date(now.getFullYear(), now.getMonth(), 1);
    } else {
      if (disablePast) {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        options.minDate = today;
      }

      if (disablePastMonth) {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        options.disable.push((date) => date < startOfMonth);
      }
    }
  }

  // Configure options based on mode
  if (mode === "datetime") {
    options.enableTime = true;
    options.time_24hr = true;
    options.enableSeconds = true;
    options.dateFormat = "Y-m-d H:i:S";
  } else if (mode === "time") {
    options.enableTime = true;
    options.noCalendar = true;
    options.dateFormat = "H:i:S";
    options.enableSeconds = true;
  } else if (mode === "monthYear") {
    options.plugins = [
      new monthSelectPlugin({
        shorthand: true,
        dateFormat: "F Y",
        altFormat: "F Y",
      }),
    ];
    options.dateFormat = "F Y";
  } else {
    options.dateFormat = "Y-m-d";
  }

  // Add multi/range select support
  if (selectMode === "multiple") {
    options.mode = "multiple";
    options.dateFormat = "Y-m-d";
  } else if (selectMode === "range") {
    options.mode = "range";
    options.dateFormat = "Y-m-d";
  }

  // Handle date change
  const handleDateChange = (selectedDates, dateStr, instance) => {
    if (!selectedDates.length) {
      return handleChange({ target: { name: field.name, value: "" } });
    }

    let formattedValue = "";

    if (mode === "monthYear") {
      const year = selectedDates[0].getFullYear();
      const month = String(selectedDates[0].getMonth() + 1).padStart(2, "0");
      formattedValue = `${year}-${month}`;
    } else if (selectMode === "multiple") {
      formattedValue = selectedDates.map((d) =>
        instance.formatDate(d, options.dateFormat)
      );
    } else if (selectMode === "range") {
      formattedValue = dateStr;
    } else {
      formattedValue = instance.formatDate(selectedDates[0], options.dateFormat);
    }

    handleChange({
      target: { name: field.name, value: formattedValue },
    });
  };

  // Parse incoming value properly for Flatpickr
  let parsedValue = value;
  if (mode === "monthYear" && typeof value === "string" && /^\d{4}-\d{2}/.test(value)) {
    const [year, month] = value.split("-");
    parsedValue = new Date(year, month - 1, 1);
  }

  return (
    <div className={field.fieldSize || "col-12"}>
      {!field.lableHide && (
        field.label ? (
          <label className="form-label text-muted">
            {field.label}{" "}
            {field.required && <span className="text-danger small">*</span>}
          </label>
        ) : (
          <label className="mt-sm-4"></label>
        )
      )}

      <Flatpickr
        className={`form-control  ${error ? "border-danger" : ""}`}
        name={field.name}
        value={parsedValue}
        onChange={handleDateChange}
        placeholder={field.placeholder || ""}
        options={{ ...options, ...props.options }}
        disabled={field.disabled || false}
      />

      {error && <span className="text-danger">{error}</span>}
    </div>
  );
};

export default FormDatePicker;
